/**
 * 手書きの小さな i18n 層（依存ゼロ）。
 *
 * 決め事:
 *   - 文言の正は en.ts。キーの型は en から作り、ja.ts は `Messages` 型で「en の全キーを持つ・余計なキーを持たない」を
 *     コンパイル時に強制する（翻訳漏れ = typecheck 失敗）。
 *   - 値は文字列か、複数形の { one, other }。複数形は Intl.PluralRules で選ぶ（日本語は常に other）。
 *   - `{name}` で差し込み。t() は文字列を返す。文中に <code> や <Link> を入れたい時は tr() に ReactNode を渡す。
 *   - 数値・日付・通貨は必ずここの locale を通した Intl で書式化する（'en-US' 直書きをしない）。
 *
 * WHY ライブラリを入れないか: 必要なのは 2 言語・差し込み・単純な複数形・Intl だけで、react-i18next 等は過剰。
 * 依存を増やさない方針（UI キットも入れていない）にも合わせる。
 *
 * 書式関数（num / ago 等）はフックの外（純関数）からも呼ばれるので、現在の locale はモジュール変数にも持つ。
 * 切り替え時は Provider がモジュール変数 → state の順で更新し、useT() を使う全コンポーネントが再描画される。
 */
import { createContext, createElement, Fragment, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { en } from './en';
import { ja } from './ja';

import { PREVIEW_NAME, PRODUCT_NAME } from '../productName';
import { CLI_NAME } from '../cliCommand';
import { isLaunchHidden } from '../launchHidden';
import { ENABLED_LOCALES, pickLocale, showLanguageSwitch, type Locale } from '../languages';
export type { Locale };
/**
 * このビルドで出す言語（languages.ts の VITE_KUMO_LANGUAGES）。既定は ['en'] だけ = 日本語は出さない
 * （founder 2026-10-05「日本語ページは完全に見えなくしてください」）。`en,ja` で今までの ['ja', 'en'] に戻る
 */
export const LOCALES: readonly Locale[] = ENABLED_LOCALES;
/** 言語の切り替えボタンを出すか（出す言語が 1 つ = 既定では出さない・2026-10-05） */
export const SHOW_LANGUAGE_SWITCH = showLanguageSwitch(LOCALES);
/** 切替ボタンの表示名（各言語の自称。どの言語で表示中でも自分の言語を見つけられるように） */
export const LOCALE_NAMES: Record<Locale, string> = { ja: '日本語', en: 'English' };

export interface PluralMsg {
  one?: string;
  other: string;
}
type En = typeof en;
export type MessageKey = keyof En;
/** ja 等の辞書の型。en の全キーが必須で、複数形キーは文字列でも { other } でもよい */
export type Messages = { [K in MessageKey]: En[K] extends string ? string : string | PluralMsg };

const DICTS: Record<Locale, Messages> = { en: en as Messages, ja };

const STORAGE_KEY = 'kumo.console.locale';

/**
 * 保存済み → navigator.languages（ja* なら ja）→ en。決め方の中身は languages.ts の pickLocale（node でテストする）。
 * 出さない言語（既定では ja・2026-10-05 founder）は保存済みでもブラウザの言語でも選ばない = 常に英語
 */
function detectLocale(): Locale {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* プライベートモード等。保存は無しで続ける */
  }
  const browser = typeof navigator !== 'undefined' ? (navigator.languages?.length ? navigator.languages : [navigator.language]) : [];
  return pickLocale({ saved, browser, enabled: LOCALES });
}

let current: Locale = detectLocale();
/** フックの外（書式関数）から今の locale を読む */
export const getLocale = () => current;
/** Intl に渡す BCP 47 タグ */
export const intlLocale = (l: Locale = current) => (l === 'ja' ? 'ja-JP' : 'en-US');

const pluralRules = new Map<Locale, Intl.PluralRules>();
function pluralOf(l: Locale, n: number) {
  let pr = pluralRules.get(l);
  if (!pr) {
    pr = new Intl.PluralRules(intlLocale(l));
    pluralRules.set(l, pr);
  }
  return pr.select(n);
}

export type Params = Record<string, string | number>;

/**
 * 呼ぶ側が渡さなくても埋める差し込み（渡せばそれが優先）:
 *   {product} = サービス名（productName.ts）・{cliName} = 入れた CLI のコマンド名（cliCommand.ts・2026-09-30 console/kumodeck）・
 *   {preview} = 検証用の MCP の名前「KUMODeck Preview (v0.2)」（productName.ts PREVIEW_NAME・2026-10-15 console/preview-name。番号を辞書に書かない = 1 か所で変える）。
 * WHY 名前の直書きを辞書から無くすか: 名前が変わるたび（KUMO X → XKumo → KUMODeck・kumo → xkumo → kumodeck）に辞書を
 *   全部なめる作業と書き戻しを無くす（wording.test.ts が直書きを落とす）。{cli} は connect の頼み文の `npx kumodeck`（cliCommand.ts の CLI）の場所で別物
 */
const AUTO_PARAMS: Readonly<Record<string, string>> = { product: PRODUCT_NAME, cliName: CLI_NAME, preview: PREVIEW_NAME };

function pick(l: Locale, key: MessageKey, params?: Params): string {
  const v = (DICTS[l][key] ?? (en as Messages)[key]) as string | PluralMsg | undefined;
  if (v === undefined) return String(key);
  if (typeof v === 'string') return v;
  const count = typeof params?.count === 'number' ? params.count : 0;
  return (pluralOf(l, count) === 'one' && v.one) || v.other;
}

/** 数値の差し込みは locale の桁区切りにする（1,234）。文字列はそのまま */
function fill(template: string, l: Locale, params?: Params): string {
  const all: Params = { ...AUTO_PARAMS, ...params };
  return template.replace(/\{(\w+)\}/g, (m, name: string) => {
    const v = all[name];
    if (v === undefined) return m;
    return typeof v === 'number' ? new Intl.NumberFormat(intlLocale(l)).format(v) : v;
  });
}

export function translate(key: MessageKey, params?: Params, l: Locale = current): string {
  return fill(pick(l, key, params), l, params);
}

/** 文中に React 要素（<code>, <Link> 等）を差し込む版。params の値が ReactNode でよい */
export function translateRich(key: MessageKey, params: Record<string, ReactNode>, l: Locale = current): ReactNode {
  const counts: Params = {};
  if (typeof params.count === 'number') counts.count = params.count;
  const template = pick(l, key, counts);
  const out: ReactNode[] = [];
  let last = 0;
  const re = /\{(\w+)\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(template))) {
    if (m.index > last) out.push(template.slice(last, m.index));
    const name = m[1]!;
    const v = params[name] === undefined ? AUTO_PARAMS[name] : params[name];
    out.push(v === undefined ? m[0] : typeof v === 'number' ? new Intl.NumberFormat(intlLocale(l)).format(v) : v);
    last = m.index + m[0].length;
  }
  if (last < template.length) out.push(template.slice(last));
  return createElement(Fragment, null, ...out.map((n, i) => createElement(Fragment, { key: i }, n)));
}

// ---------------------------------------------------------------- React

interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: MessageKey, params?: Params) => string;
  tr: (key: MessageKey, params: Record<string, ReactNode>) => ReactNode;
}

const Ctx = createContext<I18nValue | null>(null);

function applyDocument(l: Locale) {
  document.documentElement.lang = l;
  document.title = translate('app.title', undefined, l);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setState] = useState<Locale>(current);
  useEffect(() => applyDocument(locale), [locale]);
  const setLocale = useCallback((l: Locale) => {
    // 出さない言語には切り替えない（ボタンは隠しているが、呼ぶ所が増えても ja にならないように・2026-10-05 founder）
    if (!LOCALES.includes(l)) return;
    current = l; // 書式関数が次の描画で新しい locale を読むよう、state より先に更新する
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* 保存できなくても今のタブでは切り替わる */
    }
    setState(l);
  }, []);
  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale,
      t: (key, params) => translate(key, params, locale),
      tr: (key, params) => translateRich(key, params, locale)
    }),
    [locale, setLocale]
  );
  return createElement(Ctx.Provider, { value }, children);
}

export function useT() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useT outside I18nProvider');
  return v;
}

// ---------------------------------------------------------------- formatting (Intl)

const nfCache = new Map<string, Intl.NumberFormat>();
function nf(opts: Intl.NumberFormatOptions = {}) {
  const k = `${current}|${JSON.stringify(opts)}`;
  let f = nfCache.get(k);
  if (!f) {
    f = new Intl.NumberFormat(intlLocale(), opts);
    nfCache.set(k, f);
  }
  return f;
}

export const formatNumber = (n: number, opts?: Intl.NumberFormatOptions) => nf(opts).format(n);

/** 0.1 → "10%" */
export const formatPercent = (ratio: number, maximumFractionDigits = 2) => nf({ style: 'percent', maximumFractionDigits }).format(ratio);

/**
 * 相対時刻（"3 minutes ago" / "3分前"）。30 日を超えたら日付。
 * 日本語は CLDR が「3 分前」と数字の後に半角スペースを入れるが、日本語の組版では詰めるのが自然なので空白を除く。
 */
export function formatAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const s = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(s);
  if (abs < 45) return translate('time.justNow');
  const rtf = new Intl.RelativeTimeFormat(intlLocale(), { numeric: 'auto' });
  let out: string;
  if (abs < 3600) out = rtf.format(Math.round(s / 60), 'minute');
  else if (abs < 86400) out = rtf.format(Math.round(s / 3600), 'hour');
  else if (abs < 86400 * 30) out = rtf.format(Math.round(s / 86400), 'day');
  else return formatDate(iso);
  return current === 'ja' ? out.replace(/\s+/g, '') : out;
}

export const formatDate = (iso: string) => new Date(iso).toLocaleDateString(intlLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
export const formatDateTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString(intlLocale(), { dateStyle: 'medium', timeStyle: 'short' }) : '—';
/** グラフの日付ラベル（"Sep 26" / "9月26日"）。日付は UTC の暦日で来るので UTC で書く */
export const formatDay = (ymd: string) =>
  new Date(`${ymd}T00:00:00Z`).toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** 秒 → "45s" / "3m 5s" / "1h 2m"（ja: "45秒" / "3分5秒" / "1時間2分"） */
export function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return translate('time.dur.s', { s: String(s) });
  if (s < 3600) return translate('time.dur.ms', { m: String(Math.floor(s / 60)), s: String(s % 60) });
  return translate('time.dur.hm', { h: String(Math.floor(s / 3600)), m: String(Math.floor((s % 3600) / 60)) });
}

/** ApiError の既知コード → 表示文。未知のコードはサーバーの文言をそのまま出す */
const ERROR_KEYS: Record<string, MessageKey> = {
  email_taken: 'error.email_taken',
  slug_taken: 'error.slug_taken',
  slug_reserved: 'error.slug_reserved',
  invalid_request: 'error.invalid_request',
  unauthorized: 'error.unauthorized',
  forbidden: 'error.forbidden',
  not_found: 'error.not_found',
  payouts_not_configured: 'error.payouts_not_configured',
  payments_not_configured: 'error.payments_not_configured',
  payment_provider_error: 'error.payment_provider_error',
  already_owned: 'error.already_owned',
  not_refundable: 'error.not_refundable',
  rate_limited: 'error.rate_limited',
  network_error: 'error.network_error',
  route_missing: 'error.route_missing',
  internal_error: 'error.internal_error',
  invalid_token: 'error.invalid_token',
  redirect_not_allowed: 'error.redirect_not_allowed',
  secrets_not_configured: 'error.secrets_not_configured',
  provider_secret_unreadable: 'error.provider_secret_unreadable'
};

/**
 * 閉じた機能を名指しする既定のエラー文 → 閉じている間の言い方（2026-09-30 console/words-pass）。[機能のキー, 閉じている間のキー]。
 *   account_closed … 既定の文は「売上の出金はできます」まで言う。出金（payouts）を閉じている間はその半分を言わない
 * WHY errorText の中か: account_closed はプロジェクトの作成・ログイン後の各画面など、どこからでも返りうる = 呼ぶ所ごとに直すと漏れる。
 *   呼ぶ所の overrides（画面ごとの言い換え）が先に効く。切り替えは launchHidden.ts の 1 つのまま
 */
const LAUNCH_ERROR_KEYS: Record<string, readonly [string, MessageKey]> = {
  account_closed: ['payouts', 'error.account_closedLaunch']
};

export function errorText(err: { code?: string; message: string; details?: Record<string, unknown> }, overrides?: Partial<Record<string, MessageKey>>): string {
  const launch = err.code && !overrides?.[err.code] ? LAUNCH_ERROR_KEYS[err.code] : undefined;
  if (launch && isLaunchHidden(launch[0])) return translate(launch[1], errorVars(err));
  // ERROR_KEYS に無くても `error.<code>` の文言があれば使う（お金まわりのコードは en.money.ts に `error.<code>` で並べてある。
  // 表に 1 行ずつ足す手間と足し忘れを無くす）
  const dynamic = err.code ? (`error.${err.code}` as MessageKey) : null;
  const key = (err.code && (overrides?.[err.code] ?? ERROR_KEYS[err.code] ?? (dynamic && dynamic in en ? dynamic : undefined))) || null;
  return key ? translate(key, errorVars(err)) : err.message;
}

/**
 * 文言に差し込む値（2026-09-27 dashboard-review）。以前は {detail} = サーバーの英文だけで、次のことが起きていた:
 *   - invalid_request が「入力内容に誤りがあります（Request validation failed）」= 何が悪いか分からない → issues の path: message を出す
 *   - feature_disabled で「どの機能が OFF か」が出せない → details.feature / configPath
 *   - rate_limited の待つ秒数（details.retryAfter）を捨てていた
 */
function errorVars(err: { message: string; details?: Record<string, unknown> }): Record<string, string> {
  const d = err.details ?? {};
  const issues = Array.isArray(d.issues) ? (d.issues as Array<{ path?: string; message?: string }>) : [];
  const detail = issues.length
    ? issues.slice(0, 3).map((i) => (i.path ? `${i.path}: ${i.message ?? ''}` : String(i.message ?? ''))).join(' / ')
    : err.message;
  const anyOf = Array.isArray(d.anyOf) ? (d.anyOf as string[]).join(' / ') : '';
  return {
    detail,
    feature: typeof d.feature === 'string' ? d.feature : anyOf || '—',
    path: typeof d.configPath === 'string' ? d.configPath : anyOf || '—',
    seconds: typeof d.retryAfter === 'number' ? String(Math.ceil(d.retryAfter)) : '60',
    // 409 resource_limit の { kind, limit, existing }（2026-09-28 Core REV-11・環境の資源の種類ごとの上限）。無ければ ?
    kind: typeof d.kind === 'string' ? d.kind : '?',
    limit: typeof d.limit === 'number' ? String(d.limit) : '?',
    existing: typeof d.existing === 'number' ? String(d.existing) : '?'
  };
}

// 最初の描画前に <html lang> と <title> を合わせる（index.html は lang="en" 固定。一瞬英語のタイトルが出るのと、
// 読み上げが英語の発音で始まるのを防ぐ）
if (typeof document !== 'undefined') applyDocument(current);
